import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Copy, ExternalLink, Download, Share2, QrCode, Printer } from 'lucide-react';
import { BookingPosterDialog } from './BookingPosterDialog';

/** Link pubblico di prenotazione con QR code da stampare o condividere. */
export const BookingLinkCard = ({ slug, salonName, logoUrl, accent, phone, address }) => {
  const qrRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [poster, setPoster] = useState(false);

  if (!slug) {
    return (
      <Card className="card-hover h-full" data-testid="booking-link-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-outfit flex items-center gap-2">
            <QrCode className="w-4 h-4 text-primary" /> Link prenotazione online
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Scegli l'indirizzo della tua pagina di prenotazione e qui comparirà il link con il QR code
            da stampare in salone.
          </p>
          <Button asChild variant="outline" className="min-h-[44px]" data-testid="booking-link-setup-btn">
            <Link to="/settings">Imposta l'indirizzo</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const url = `${window.location.origin}/prenota/${slug}`;

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copiato');
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `qr-prenotazioni-${slug}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('QR code scaricato');
  };

  const share = async () => {
    const text = `Prenota da ${salonName || 'noi'}: ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: salonName || 'Prenota online', text, url });
        return;
      } catch {
        return;
      }
    }
    navigator.clipboard.writeText(text);
    toast.success('Messaggio copiato, incollalo dove vuoi');
  };

  return (
    <Card className="card-hover h-full" data-testid="booking-link-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-outfit flex items-center gap-2">
          <QrCode className="w-4 h-4 text-primary" /> Link prenotazione online
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div ref={qrRef} className="bg-white p-2.5 rounded-xl shrink-0" data-testid="booking-qr">
            <QRCodeCanvas value={url} size={116} level="M" includeMargin={false} />
          </div>
          <div className="min-w-0 flex-1 w-full">
            <p className="text-xs text-muted-foreground mb-1">I clienti prenotano da qui</p>
            <p className="text-sm font-medium break-all" data-testid="booking-url">{url}</p>
            <p className="text-xs text-muted-foreground mt-2">
              Stampa il QR e tienilo alla cassa: si inquadra e si prenota in dieci secondi.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={copy} className="min-h-[44px]" data-testid="copy-booking-link">
            <Copy className="w-4 h-4 mr-2" />{copied ? 'Copiato' : 'Copia link'}
          </Button>
          <Button variant="outline" onClick={share} className="min-h-[44px]" data-testid="share-booking-link">
            <Share2 className="w-4 h-4 mr-2" />Condividi
          </Button>
          <Button variant="outline" onClick={downloadQr} className="min-h-[44px]" data-testid="download-qr">
            <Download className="w-4 h-4 mr-2" />Scarica QR
          </Button>
          <Button variant="outline" asChild className="min-h-[44px]">
            <a href={url} target="_blank" rel="noreferrer" data-testid="open-booking-link">
              <ExternalLink className="w-4 h-4 mr-2" />Apri
            </a>
          </Button>
        </div>

        <Button onClick={() => setPoster(true)} className="w-full min-h-[44px]" data-testid="open-poster">
          <Printer className="w-4 h-4 mr-2" />Crea locandina da stampare
        </Button>

        <BookingPosterDialog
          open={poster} onOpenChange={setPoster} url={url} salonName={salonName}
          logoUrl={logoUrl} accent={accent} phone={phone} address={address}
        />
      </CardContent>
    </Card>
  );
};
